import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Canvas, FabricObject} from 'fabric';
import {useDndMonitor} from '@dnd-kit/core';
import {FloorPlanItem, ShapeType} from '../types';
import {DraggableElementType} from '../edit/EditSidebar';
import {generateId} from "../../../../../utils/generate-id";
import {
    createDefaultCircleShapeItem,
    createDefaultCircleTableItem,
    createDefaultEllipseShapeItem,
    createDefaultHexagonShapeItem,
    createDefaultHorizontalWallItem,
    createDefaultOctagonShapeItem,
    createDefaultRectangleTableItem,
    createDefaultRectShapeItem,
    createDefaultTextItem,
    createDefaultTriangleShapeItem,
    createDefaultVerticalWallItem,
    createShapeElement,
    createTableGroup,
    createTextElement,
    createWallElement
} from "../utils/fabric-factory";
import {snapPoint} from "../utils/snap-geometry";

interface UseCanvasDropOptions {
    canvas: Canvas | null;
    containerRef: React.RefObject<HTMLDivElement | null>;
    showSeatsAroundTable: boolean;
    onElementDropped: (element: FabricObject, id: string) => void;
}

const countElementsByType = (canvas: Canvas, kind: 'shape' | 'wall', shape?: ShapeType): number => {
    return canvas.getObjects().filter(obj => {
        const item = obj.floorPlanItem;
        if (!item?.element) return false;
        if (item.element.kind !== kind) return false;
        if (kind === 'shape' && shape && item.element.kind === 'shape' && item.element.shape !== shape) return false;
        return true;
    }).length;
};

const generateElementLabel = (
    canvas: Canvas,
    kind: 'shape' | 'wall',
    shape: ShapeType | undefined,
    labels: Record<ShapeType | 'wall', string>
): string => {
    const count = countElementsByType(canvas, kind, shape);
    const label = kind === 'wall' ? labels.wall : labels[shape!];
    return `${label} ${count + 1}`;
};

const withLabel = (item: FloorPlanItem, label: string): FloorPlanItem => ({
    ...item,
    element: item.element ? {
        ...item.element,
        canvas: {...item.element.canvas, label} as any,
    } : undefined,
});

const isPointInRect = (x: number, y: number, rect: DOMRect): boolean => {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
};

/**
 * Hook to handle canvas drop - must be used inside DndContext
 */
export function useCanvasDrop({
                                  canvas,
                                  containerRef,
                                  showSeatsAroundTable,
                                  onElementDropped,
                              }: UseCanvasDropOptions) {
    const {t} = useTranslation();
    const [isDragOver, setIsDragOver] = useState(false);
    const pointerPos = useRef<{ x: number; y: number } | null>(null);

    // Translated labels for elements
    const elementLabels: Record<ShapeType | 'wall', string> = {
        rect: t('Rectangle'),
        circle: t('Circle'),
        ellipse: t('Ellipse'),
        triangle: t('Triangle'),
        hexagon: t('Hexagon'),
        octagon: t('Octagon'),
        wall: t('Wall'),
    };

    // Translated table names
    const tableLabel = t('Table');
    const roundTableLabel = t('Round Table');
    const textLabel = t('Text');

    useDndMonitor({
        onDragMove(event) {
            const container = containerRef.current;
            if (!container) return;

            const rect = event.active.rect.current.translated;
            if (rect) {
                const x = rect.left + rect.width / 2;
                const y = rect.top + rect.height / 2;
                pointerPos.current = {x, y};

                const containerRect = container.getBoundingClientRect();
                setIsDragOver(isPointInRect(x, y, containerRect));
            }
        },
        onDragEnd(event) {
            const container = containerRef.current;
            if (!canvas || !container || !pointerPos.current) {
                setIsDragOver(false);
                pointerPos.current = null;
                return;
            }

            const containerRect = container.getBoundingClientRect();

            if (!isPointInRect(pointerPos.current.x, pointerPos.current.y, containerRect)) {
                setIsDragOver(false);
                pointerPos.current = null;
                return;
            }

            const elementType = event.active.data.current?.type as DraggableElementType;
            if (!elementType) {
                setIsDragOver(false);
                pointerPos.current = null;
                return;
            }

            const viewportX = pointerPos.current.x - containerRect.left;
            const viewportY = pointerPos.current.y - containerRect.top;

            const vpt = canvas.viewportTransform;
            if (!vpt) {
                setIsDragOver(false);
                pointerPos.current = null;
                return;
            }

            const snappedPoint = snapPoint({
                x: (viewportX - vpt[4]) / vpt[0],
                y: (viewportY - vpt[5]) / vpt[3],
            });
            const x = snappedPoint.x;
            const y = snappedPoint.y;

            const id = generateId();
            let element: FabricObject | null = null;

            switch (elementType) {
                case 'table-rectangle':
                    element = createTableGroup(createDefaultRectangleTableItem(id, x, y, tableLabel), showSeatsAroundTable);
                    break;
                case 'table-circle':
                    element = createTableGroup(createDefaultCircleTableItem(id, x, y, roundTableLabel), showSeatsAroundTable);
                    break;
                case 'shape-rect':
                    element = createShapeElement(withLabel(createDefaultRectShapeItem(id, x, y), generateElementLabel(canvas, 'shape', 'rect', elementLabels)));
                    break;
                case 'shape-circle':
                    element = createShapeElement(withLabel(createDefaultCircleShapeItem(id, x, y), generateElementLabel(canvas, 'shape', 'circle', elementLabels)));
                    break;
                case 'shape-ellipse':
                    element = createShapeElement(withLabel(createDefaultEllipseShapeItem(id, x, y), generateElementLabel(canvas, 'shape', 'ellipse', elementLabels)));
                    break;
                case 'shape-triangle':
                    element = createShapeElement(withLabel(createDefaultTriangleShapeItem(id, x, y), generateElementLabel(canvas, 'shape', 'triangle', elementLabels)));
                    break;
                case 'shape-hexagon':
                    element = createShapeElement(withLabel(createDefaultHexagonShapeItem(id, x, y), generateElementLabel(canvas, 'shape', 'hexagon', elementLabels)));
                    break;
                case 'shape-octagon':
                    element = createShapeElement(withLabel(createDefaultOctagonShapeItem(id, x, y), generateElementLabel(canvas, 'shape', 'octagon', elementLabels)));
                    break;
                case 'text':
                    element = createTextElement(createDefaultTextItem(id, x, y, textLabel));
                    break;
                case 'wall-vertical':
                    element = createWallElement(withLabel(createDefaultVerticalWallItem(id, x, y), generateElementLabel(canvas, 'wall', undefined, elementLabels)));
                    break;
                case 'wall-horizontal':
                    element = createWallElement(withLabel(createDefaultHorizontalWallItem(id, x, y), generateElementLabel(canvas, 'wall', undefined, elementLabels)));
                    break;
            }

            if (element) {
                canvas.add(element);
                canvas.setActiveObject(element);
                canvas.renderAll();
                onElementDropped(element, id);
            }

            setIsDragOver(false);
            pointerPos.current = null;
        },
        onDragCancel() {
            setIsDragOver(false);
            pointerPos.current = null;
        },
    });

    // Visual feedback
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        if (isDragOver) {
            container.style.outline = '2px dashed';
            container.style.outlineOffset = '-2px';
        } else {
            container.style.outline = 'none';
        }
    }, [isDragOver, containerRef]);

    return {isDragOver};
}
